(() => {
  const US_DUVET_SIZES = new Set([
    'Twin',
    'XL Twin',
    'Full',
    'Queen',
    'California King',
    'Imperial King',
    'Alaskan King',
  ]);
  const UK_DUVET_SIZES = new Set(['Double', 'Superking', 'Superking Extra Long', 'Emperor']);
  const US_PILLOW_SIZES = new Set(['Queen']);

  const FLAT_SHEET_DIMENSIONS = {
    Single: '180 × 290 cm (71 × 114 in)',
    Double: '240 × 290 cm (95 × 114 in)',
    Queen: '240 × 290 cm (95 × 114 in)',
    King: '270 × 290 cm (106 × 114 in)',
    Superking: '270 × 290 cm (106 × 114 in)',
    'California King': '270 × 290 cm (106 × 114 in)',
    Emperor: '300 × 290 cm (118 × 114 in)',
  };

  const DUVET_DIMENSIONS = {
    US: {
      Twin: '150 × 229 cm (59 × 90 in)',
      'XL Twin': '180 × 239 cm (71 × 94 in)',
      Full: '206 × 219 cm (81 × 86 in)',
      Queen: '229 × 239 cm (90 × 94 in)',
      King: '274 × 239 cm (108 × 94 in)',
      'California King': '260 × 239 cm (102 × 94 in)',
      'Imperial King': '290 × 239 cm (114 × 94 in)',
      'Alaskan King': '350 × 300 cm (138 × 118 in)',
    },
    UK: {
      Single: '140 × 200 cm (55 × 79 in)',
      Double: '200 × 200 cm (79 × 79 in)',
      King: '230 × 220 cm (91 × 87 in)',
      Superking: '260 × 220 cm (102 × 87 in)',
      'Superking Extra Long': '260 × 240 cm (102 × 94.5 in)',
      Emperor: '290 × 235 cm (114 × 93 in)',
    },
    NOR: {
      Single: '140 × 220 cm (55 × 87 in)',
      King: '240 × 220 cm (95 × 87 in)',
    },
    EUR: {
      Single: '140 × 220 cm (55 × 87 in)',
      King: '240 × 220 cm (95 × 87 in)',
    },
  };

  const PILLOW_DIMENSIONS = {
    US: {
      Boudoir: '31 × 41 cm (12 × 16 in)',
      'Boudoir XL': '45 × 50 cm (18 × 20 in)',
      Standard: '50 × 66 cm (20 × 26 in)',
      Queen: '50 × 76 cm (20 × 30 in)',
      King: '50 × 90 cm (20 × 36 in)',
      'Euro/Square': '65 × 65 cm (26 × 26 in)',
    },
    UK: {
      Boudoir: '31 × 41 cm (12 × 16 in)',
      'Boudoir XL': '45 × 50 cm (18 × 20 in)',
      Standard: '50 × 75 cm (20 × 30 in)',
      King: '50 × 90 cm (20 × 36 in)',
      'Euro/Square': '65 × 65 cm (26 × 26 in)',
    },
    NOR: {
      Boudoir: '31 × 41 cm (12 × 16 in)',
      'Boudoir XL': '45 × 50 cm (18 × 20 in)',
      Standard: '50 × 70 cm (20 × 28 in)',
      King: '50 × 90 cm (20 × 36 in)',
      'Euro/Square': '65 × 65 cm (26 × 26 in)',
    },
    EUR: {
      Boudoir: '31 × 41 cm (12 × 16 in)',
      'Boudoir XL': '45 × 50 cm (18 × 20 in)',
      Standard: '50 × 70 cm (20 × 28 in)',
      King: '50 × 90 cm (20 × 36 in)',
      'Euro/Square': '65 × 65 cm (26 × 26 in)',
    },
  };

  function countryToRegion(countryIso) {
    if (countryIso === 'GB') return 'UK';
    if (countryIso === 'US') return 'US';
    if (countryIso === 'NO') return 'NOR';
    return 'EUR';
  }

  function getActiveProductRegion(form, config) {
    const picker = form.closest('product-info')?.querySelector('[data-variant-region-picker]');
    if (picker?.dataset.selectedRegion) return picker.dataset.selectedRegion;
    return config.defaultProductRegion || countryToRegion(config.country);
  }

  function regionFromAllowedCountries(allowedCountries, fallbackCountry) {
    if (!Array.isArray(allowedCountries) || allowedCountries.length === 0) {
      return countryToRegion(fallbackCountry);
    }

    const normalized = allowedCountries.map((entry) => String(entry).toUpperCase());
    const fallbackRegion = countryToRegion(fallbackCountry);

    if (normalized.length === 1) {
      const only = normalized[0];
      if (only === 'US') return 'US';
      if (only === 'UK' || only === 'GB') return 'UK';
      if (only === 'NO') return 'NOR';
      if (only === 'EU' || only === 'EURO') return 'EUR';
    }

    if (normalized.includes('US') && fallbackRegion === 'US') return 'US';
    if ((normalized.includes('UK') || normalized.includes('GB')) && fallbackRegion === 'UK') return 'UK';
    if (normalized.includes('NO') && fallbackRegion === 'NOR') return 'NOR';
    if ((normalized.includes('EU') || normalized.includes('EURO')) && fallbackRegion === 'EUR') return 'EUR';

    if (normalized.includes('US')) return 'US';
    if (normalized.includes('UK') || normalized.includes('GB')) return 'UK';
    if (normalized.includes('NO')) return 'NOR';
    return 'EUR';
  }

  function parseEmbeddedSizeValue(sizeValue) {
    const match = String(sizeValue).match(
      /(\d+)\s*x\s*(\d+)\s*cm\s*\((\d+(?:\.\d+)?)"?\s*x\s*(\d+(?:\.\d+)?)"?\)/i
    );
    if (!match) return null;
    return `${match[1]} × ${match[2]} cm (${match[3]} × ${match[4]} in)`;
  }

  function pickFromMultiRegionMetafield(value, region) {
    if (!value || !String(value).includes('|')) return value || '';
    const parts = String(value)
      .split('|')
      .map((part) => part.trim())
      .filter(Boolean);

    const matchers = {
      UK: /^UK\s*:/i,
      NOR: /^NO(R(WAY)?)?\s*:/i,
      EUR: /^EU(\/NOR)?\s*:/i,
      US: /^US\s*:/i,
    };

    let match = parts.find((part) => matchers[region]?.test(part));
    if (!match && (region === 'NOR' || region === 'EUR')) {
      match = parts.find((part) => matchers.EUR.test(part) || matchers.NOR.test(part));
    }

    if (!match) return value.trim();

    return match.replace(/^[^:]+:\s*/, '').trim();
  }

  function resolveDuvetRegion(sizeValue, allowedCountries, productRegion) {
    if (US_DUVET_SIZES.has(sizeValue)) return 'US';
    if (UK_DUVET_SIZES.has(sizeValue)) return 'UK';
    return productRegion || regionFromAllowedCountries(allowedCountries, 'US');
  }

  function resolvePillowRegion(sizeValue, allowedCountries, productRegion) {
    if (US_PILLOW_SIZES.has(sizeValue)) return 'US';
    return productRegion || regionFromAllowedCountries(allowedCountries, 'US');
  }

  function isFlatSheetMode(config) {
    return config?.dimensionMode === 'flat_sheet';
  }

  function lookupDimensions(productType, sizeValue, lineProductType, allowedCountries, productRegion, config) {
    if (!sizeValue) return '';

    if (config && isFlatSheetMode(config)) {
      return FLAT_SHEET_DIMENSIONS[sizeValue] || '';
    }

    const embedded = parseEmbeddedSizeValue(sizeValue);
    if (embedded) return embedded;

    const normalizedType = String(productType || '').toLowerCase();
    const normalizedLineType = String(lineProductType || '').toLowerCase();
    const usePillowTable =
      normalizedType.includes('pillow') || normalizedLineType.includes('pillow');

    if (usePillowTable) {
      const region = resolvePillowRegion(sizeValue, allowedCountries, productRegion);
      return PILLOW_DIMENSIONS[region]?.[sizeValue] || '';
    }

    if (normalizedType.includes('duvet') || normalizedType.includes('bed linen')) {
      const region = resolveDuvetRegion(sizeValue, allowedCountries, productRegion);
      return DUVET_DIMENSIONS[region]?.[sizeValue] || '';
    }

    return '';
  }

  function regionToDisplayPrefix(region) {
    if (region === 'UK') return 'UK';
    if (region === 'US') return 'US';
    if (region === 'NOR') return 'NO';
    return 'EU';
  }

  function regionalSizeLabel(variant, config, productRegion) {
    if (!variant?.size) return '';

    if (isFlatSheetMode(config)) {
      return variant.size;
    }

    const normalizedType = String(config.productType || '').toLowerCase();
    const normalizedLineType = String(variant.lineProductType || '').toLowerCase();
    const usePillowTable =
      normalizedType.includes('pillow') || normalizedLineType.includes('pillow');

    const region = usePillowTable
      ? resolvePillowRegion(variant.size, variant.allowedCountries, productRegion)
      : resolveDuvetRegion(variant.size, variant.allowedCountries, productRegion);

    return `${regionToDisplayPrefix(region)} ${variant.size}`;
  }

  function customerDimensions(variant, config, productRegion) {
    const metafieldValue = variant.dimensionsMetafield?.trim();
    if (metafieldValue) {
      return pickFromMultiRegionMetafield(metafieldValue, productRegion);
    }

    return lookupDimensions(
      config.productType,
      variant.size,
      variant.lineProductType,
      variant.allowedCountries,
      productRegion,
      config
    );
  }

  function customerDimensionsProperty(variant, config, productRegion) {
    const sizeLabel = regionalSizeLabel(variant, config, productRegion);
    const dimensions = customerDimensions(variant, config, productRegion);

    if (isFlatSheetMode(config)) {
      if (sizeLabel && dimensions) return `${sizeLabel} ${dimensions}`;
      return dimensions || sizeLabel;
    }

    if (sizeLabel && dimensions) return `${sizeLabel} ${dimensions}`;
    return sizeLabel || dimensions;
  }

  function readConfig(form) {
    const script = form.querySelector('[data-variant-dimensions-config]');
    if (!script) return null;

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      return null;
    }
  }

  function specificationsProperty(form, config) {
    const fromConfig = String(config?.specifications || '').trim();
    const fromInput = form.querySelector('[data-variant-specifications-input]')?.value?.trim() || '';
    const fromDom =
      form.closest('product-info')?.querySelector('.product-eyebrow')?.textContent?.trim() || '';
    return fromConfig || fromInput || fromDom;
  }

  function updateForm(form, config) {
    const variantInput = form.querySelector('input[name="id"]');
    const dimensionsInput = form.querySelector('[data-variant-dimensions-input]');
    const specInput = form.querySelector('[data-variant-specifications-input]');

    if (dimensionsInput && variantInput && config?.variants) {
      const variant = config.variants[String(variantInput.value)];
      const productRegion = getActiveProductRegion(form, config);
      dimensionsInput.value = variant ? customerDimensionsProperty(variant, config, productRegion) : '';

      const display = form.closest('product-info')?.querySelector('[data-variant-dimensions-display]');
      if (display) {
        const dimensions = variant ? customerDimensions(variant, config, productRegion) : '';
        display.textContent = dimensions ? dimensions : '';
        display.hidden = !dimensions;
      }
    }

    if (specInput) {
      specInput.value = specificationsProperty(form, config);
    }
  }

  function initForm(form) {
    const config = readConfig(form) || {};

    const refresh = () => updateForm(form, config);
    refresh();
    form.querySelector('input[name="id"]')?.addEventListener('change', refresh);
    form.addEventListener('submit', refresh, true);
    document.addEventListener('variant-region:change', refresh);
  }

  function init() {
    document.querySelectorAll('form[data-type="add-to-cart-form"]').forEach((form) => {
      if (form.querySelector('[data-variant-dimensions-input], [data-variant-specifications-input]')) {
        initForm(form);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);

  // Shared lookups for other components (e.g. the Couples bundle configurator).
  window.NorvegrDimensions = {
    pickFromMultiRegionMetafield,
    countryToRegion,
    regionToDisplayPrefix,
    FLAT_SHEET_DIMENSIONS,
    DUVET_DIMENSIONS,
    PILLOW_DIMENSIONS,
  };
})();
